<template>
  <div class="v2026-sysnote-group">
    <div class="v2026-sysnote-section-head">
      <span class="v2026-sysnote-section-label">โน้ตในระบบ</span>
      <template v-if="entityId && !systemNotesLoading">
        <button v-if="systemNotes.length" type="button" class="v2026-sysnote-icon-btn"
                @click="openAddNote" aria-label="เพิ่มโน้ตในระบบ">
          <!-- The pencil genuinely differs per panel: stroke-based <img> vs CSS-mask span. -->
          <span v-if="registry.editIconMask" class="v2026-sysnote-mask v2026-sysnote-edit" aria-hidden="true"
                :style="maskStyle(registry.editIcon)"></span>
          <img v-else class="v2026-sysnote-edit-img" aria-hidden="true" :src="registry.editIcon" alt="">
        </button>
        <button v-else type="button" class="v2026-sysnote-add"
                @click="openAddNote" aria-label="เพิ่มโน้ตในระบบ">
          <span class="v2026-sysnote-add__ico" aria-hidden="true" :style="maskStyle(icons.plus)"></span>
          <span class="v2026-sysnote-add__text">เพิ่ม</span>
        </button>
      </template>
    </div>
    <LoadingV2026 v-if="systemNotesLoading" label="กำลังโหลด" />
    <!-- An empty list renders NOTHING below the head — that is intended, not a missing branch. -->
    <template v-else-if="systemNotes.length">
      <div v-for="note in systemNotes" :key="'sysnote_' + note.id"
           class="v2026-sysnote-card v2026-sysnote-note">
        <div class="v2026-sysnote-note__head">
          <img class="v2026-sysnote-avatar-xs" :src="noteAuthorAvatar(note)" alt=""
               @error="onNoteAvatarError">
          <span class="v2026-sysnote-note__author">{{ noteAuthorName(note) }}</span>
          <span v-if="notePLabel(note)" class="v2026-sysnote-note__p">{{ notePLabel(note) }}</span>
        </div>
        <div class="v2026-sysnote-note__text">{{ note.comment }}</div>
        <img v-if="noteThumb(note)" class="v2026-sysnote-note__thumb" :src="noteThumb(note)" alt="">
        <div class="v2026-sysnote-note__date">{{ note.created_at | prettyDate }}</div>
      </div>
    </template>

    <div v-if="showNoteModal" class="v2026-sysnote-modal-overlay" @click.self="closeNoteModal">
      <div class="v2026-sysnote-modal" role="dialog" aria-modal="true">
        <div class="v2026-sysnote-modal__header">
          <span class="v2026-sysnote-modal__title">โน้ตในระบบ</span>
          <button type="button" class="v2026-sysnote-modal__close" @click="closeNoteModal" aria-label="ปิด">✕</button>
        </div>
        <div class="v2026-sysnote-modal__body">
          <template v-if="registry.requiresPValue">
            <label class="v2026-sysnote-modal__label" :for="pFieldId">P</label>
            <select :id="pFieldId" class="v2026-sysnote-modal__select" v-model="noteForm.pValue">
              <option value="" disabled>เลือก P</option>
              <option v-for="p in pValues" :key="p" :value="p">{{ p | pValue }}</option>
            </select>
          </template>

          <label class="v2026-sysnote-modal__label" :for="commentFieldId">เพิ่มโน้ตที่คุณต้องการ</label>
          <textarea :id="commentFieldId" class="v2026-sysnote-modal__ta"
                    v-model="noteForm.comment" placeholder="พิมพ์โน้ต…"></textarea>

          <div class="v2026-sysnote-modal__meta">
            <label class="v2026-sysnote-modal__upload">
              <span class="v2026-sysnote-mask v2026-sysnote-modal__upload-ico" aria-hidden="true"
                    :style="maskStyle(icons.attach)"></span>
              <span>แนบรูป</span>
              <span v-if="noteFiles.length" class="v2026-sysnote-modal__filecount">{{ noteFiles.length }}</span>
              <!-- The applicant endpoint is upload.single(), so NO multiple attribute here. -->
              <input type="file" accept="image/*" :multiple="registry.fileMultiple"
                     @change="onNoteFiles" hidden>
            </label>
            <div class="v2026-sysnote-modal__admin">
              <img class="v2026-sysnote-avatar-xs" :src="currentAdminAvatar" alt="">
              <span class="v2026-sysnote-modal__admin-name">{{ currentAdminName }}</span>
            </div>
          </div>

          <div class="v2026-sysnote-modal__actions">
            <button type="button" class="v2026-sysnote-modal__submit"
                    :disabled="savingNote || !canSaveNote"
                    @click="saveSystemNote">{{ savingNote ? 'กำลังบันทึก…' : 'บันทึก' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import LoadingV2026 from './LoadingV2026.vue'

// Defaults chosen so types 0 and 1 reproduce the OLD code path key-for-key — those two panels
// must keep emitting byte-identical requests.
const REGISTRY_DEFAULTS = {
  urlMode: 'path', // 'path' → /beneat/{basePath}/{id}/note ; 'query' → a fixed URL + a query param
  listUrl: null, // urlMode 'query' only
  saveUrl: null, // urlMode 'query' only
  queryKey: null, // urlMode 'query' only — the GET param carrying the entity id
  responseKey: 'data', // res.data.<key> holds the note array
  fileField: 'files[]', // FormData key for attachments
  fileMultiple: true, // <input multiple> + append every file
  requiresPValue: false, // adds the P select to the modal and gates บันทึก on it
  flatThumbKey: null, // note[<key>] is a bare URL string (vs the thumbKeys array chain)
  authorNameKey: null // note[<key>] is a flat author name (vs note.admin.name)
}

// Live note contracts. Every field was read off the real call sites; changing any of them
// changes a live request.
const TYPE_REGISTRY = {
  0: {
    name: 'customer',
    basePath: 'users',
    idKey: 'user_id',
    // The customer branch never sent eloquent, and its POST carried no axios config at all.
    eloquent: false,
    saveConfig: null,
    thumbKeys: ['user_note_pictures'],
    // Stroke-based icon rendered as a normal <img>, NOT a mask.
    editIcon: require('@/assets/icons2026/pencil-edit.svg'),
    editIconMask: false
  },
  1: {
    name: 'professional',
    basePath: 'professionals',
    idKey: 'professional_id',
    // The professional branch appends eloquent=1 and passes withCredentials:false on both calls.
    eloquent: true,
    saveConfig: { withCredentials: false },
    thumbKeys: ['prof_note_picture', 'professional_note_pictures', 'user_note_pictures'],
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true
  },
  2: {
    name: 'applicant',
    basePath: null,
    urlMode: 'query',
    listUrl: '/beneat/professionals/register/note',
    saveUrl: '/beneat/professionals/register/note',
    queryKey: 'professional_register_id',
    idKey: 'professional_register_id',
    responseKey: 'notes',
    eloquent: false,
    saveConfig: null,
    thumbKeys: [],
    fileField: 'image_url',
    fileMultiple: false,
    requiresPValue: true,
    flatThumbKey: 'image_url',
    authorNameKey: 'admin_name',
    editIcon: require('@/assets/icons2026/edit.svg'),
    editIconMask: true
  }
}

const P_VALUES = [100, 90, 80, 50, 40, 0]

export default {
  name: 'system-notes-v2026',
  components: {
    LoadingV2026
  },
  props: {
    type: {
      type: Number,
      default: 0
    },
    entityId: {
      type: Number,
      default: null
    },
    admin: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      systemNotes: [],
      systemNotesLoading: false, // GET in flight → loading line
      noteForm: { comment: '', pValue: '' }, // add-note modal input
      pValues: P_VALUES,
      noteFiles: [], // optional attachments for the new note
      savingNote: false, // POST in flight → disable submit
      showNoteModal: false, // custom add-note modal visibility
      placeholderAvatar: 'https://beneat.co/static/img/unknown.png',
      icons: {
        attach: require('@/assets/icons2026/composer-attach.svg'),
        plus: require('@/assets/icons2026/plus.svg')
      }
    }
  },
  computed: {
    registry () {
      return Object.assign({}, REGISTRY_DEFAULTS, TYPE_REGISTRY[this.type] || TYPE_REGISTRY[0])
    },
    canSaveNote () {
      if (!this.noteForm.comment) return false
      if (this.registry.requiresPValue && this.noteForm.pValue === '') return false
      return true
    },
    commentFieldId () {
      return 'v2026-sysnote-comment-' + this.registry.name
    },
    pFieldId () {
      return 'v2026-sysnote-p-' + this.registry.name
    },
    currentAdminName () {
      return (this.admin && this.admin.name) ? this.admin.name : ''
    },
    currentAdminAvatar () {
      return (this.admin && this.admin.profile_picture) ? this.admin.profile_picture : this.placeholderAvatar
    }
  },
  watch: {
    entityId: {
      handler (id) {
        if (id) {
          this.fetchSystemNotes(id)
        } else {
          this.systemNotes = []
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetchSystemNotes (id) {
      if (!id) { this.systemNotes = []; return }
      const reg = this.registry
      this.systemNotesLoading = true
      try {
        const url = reg.urlMode === 'query' ? reg.listUrl : `/beneat/${reg.basePath}/${id}/note`
        const config = reg.urlMode === 'query'
          ? { params: { [reg.queryKey]: id }, withCredentials: false }
          : { withCredentials: false }
        const { data } = await axios.get(url, config)
        const list = data ? data[reg.responseKey] : null
        this.systemNotes = Array.isArray(list) ? list : []
      } catch (e) {
        this.systemNotes = []
      } finally {
        this.systemNotesLoading = false
      }
    },
    openAddNote () {
      this.noteForm.comment = ''
      this.noteForm.pValue = ''
      this.noteFiles = []
      this.showNoteModal = true
    },
    closeNoteModal () {
      this.showNoteModal = false
    },
    onNoteFiles (e) {
      const files = e.target.files
      const picked = (files && files.length) ? Array.from(files) : []
      this.noteFiles = this.registry.fileMultiple ? picked : picked.slice(0, 1)
    },
    async saveSystemNote () {
      const id = this.entityId
      if (!id || !this.canSaveNote) return
      const reg = this.registry
      this.savingNote = true
      try {
        const formData = new FormData()
        let url
        if (reg.urlMode === 'query') {
          if (this.noteFiles.length) {
            formData.append(reg.fileField, this.noteFiles[0])
          }
          formData.append('comment', this.noteForm.comment)
          formData.append('p_value', this.noteForm.pValue)
          formData.append('admin_id', this.admin ? this.admin.beneat_admin_id : '')
          formData.append(reg.idKey, id)
          url = reg.saveUrl
        } else {
          formData.append(reg.idKey, id)
          formData.append('admin_id', this.admin ? this.admin.beneat_admin_id : '')
          formData.append('comment', this.noteForm.comment)
          if (reg.eloquent) {
            formData.append('eloquent', 1)
          }
          for (let i = 0; i < this.noteFiles.length; i++) {
            formData.append(reg.fileField, this.noteFiles[i])
          }
          url = `/beneat/${reg.basePath}/${id}/note/save`
        }
        await (reg.saveConfig ? axios.post(url, formData, reg.saveConfig) : axios.post(url, formData))
        this.showNoteModal = false
        this.noteForm.comment = ''
        this.noteForm.pValue = ''
        this.noteFiles = []
        await this.fetchSystemNotes(id)
      } catch (e) {
      } finally {
        this.savingNote = false
      }
    },
    noteAuthorName (note) {
      const key = this.registry.authorNameKey
      if (key) return note[key] ? note[key] : '-'
      return (note.admin && note.admin.name) ? note.admin.name : '-'
    },
    noteAuthorAvatar (note) {
      return (note.admin && note.admin.picture) ? note.admin.picture : this.placeholderAvatar
    },
    onNoteAvatarError (event) {
      if (event && event.target && event.target.src !== this.placeholderAvatar) {
        event.target.src = this.placeholderAvatar
      }
    },
    noteThumb (note) {
      const flat = this.registry.flatThumbKey
      if (flat) {
        const url = note[flat]
        return (url != null && String(url).trim() !== '') ? url : null
      }
      const pics = this.registry.thumbKeys.reduce((acc, key) => acc || note[key], null)
      return (Array.isArray(pics) && pics.length && pics[0].picture_url) ? pics[0].picture_url : null
    },
    notePLabel (note) {
      if (!this.registry.requiresPValue) return ''
      return this.$options.filters.pValue(note.p_value)
    },
    maskStyle (url) {
      return {
        '-webkit-mask-image': 'url(' + url + ')',
        'mask-image': 'url(' + url + ')'
      }
    }
  }
}
</script>

<style scoped>
.v2026-sysnote-mask {
  display: inline-block;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-icon);
}

.v2026-sysnote-section-label {
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  color: var(--v2026-text-secondary);
}

.v2026-sysnote-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v2026-sysnote-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
}
.v2026-sysnote-icon-btn:hover {
  background: var(--v2026-surface-hover);
}
.v2026-sysnote-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-sysnote-edit {
  width: 16px;
  height: 16px;
  background-color: var(--v2026-icon);
}
.v2026-sysnote-edit-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
}

.v2026-sysnote-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px 0 6px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-section);
  font-weight: 600;
  cursor: pointer;
}
.v2026-sysnote-add:hover {
  background: rgba(49, 193, 215, 0.12);
}
.v2026-sysnote-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}
.v2026-sysnote-add__ico {
  display: inline-block;
  width: 14px;
  height: 14px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-sysnote-card {
  background: var(--v2026-surface-sunken);
  border-radius: var(--v2026-r-card);
}

.v2026-sysnote-group {
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-sysnote-note {
  padding: var(--v2026-sp-3);
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-2);
}
.v2026-sysnote-note__head {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-sysnote-avatar-xs {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}
.v2026-sysnote-note__author {
  flex: 1;
  min-width: 0;
  font-size: var(--v2026-fs-meta);
  font-weight: 600;
  color: var(--v2026-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2026-sysnote-note__p {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--v2026-color-main-soft);
  color: var(--v2026-text);
  font-size: var(--v2026-fs-meta);
  white-space: nowrap;
}
.v2026-sysnote-note__text {
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  line-height: 1.5;
  word-break: break-word;
}
.v2026-sysnote-note__thumb {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: var(--v2026-surface-hover);
  object-fit: cover;
}

.v2026-sysnote-note__date {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-muted);
}

.v2026-sysnote-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.45);
}

.v2026-sysnote-modal {
  width: 100%;
  max-width: 701px;
  min-height: 437px;
  display: flex;
  flex-direction: column;
  border: 1px solid #EBEDF0;
  border-radius: 24px;
  background: #F8F9FA;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.18);
}

.v2026-sysnote-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
}
.v2026-sysnote-modal__title {
  font-size: 18px;
  font-weight: 700;
  color: var(--v2026-text);
}
.v2026-sysnote-modal__close {
  border: 0;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: var(--v2026-text-secondary);
  cursor: pointer;
  padding: 4px;
}
.v2026-sysnote-modal__close:hover {
  color: var(--v2026-text);
}

.v2026-sysnote-modal__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: var(--v2026-sp-3);
  padding: 24px;
}
.v2026-sysnote-modal__label {
  font-size: var(--v2026-fs-section);
  color: var(--v2026-text-secondary);
}

.v2026-sysnote-modal__select {
  width: 100%;
  height: 44px;
  padding: 0 var(--v2026-sp-3);
  border: 1px solid #EBEDF0;
  border-radius: 8px;
  background: #FFFFFF;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
}
.v2026-sysnote-modal__select:focus {
  outline: none;
  border-color: var(--v2026-color-main);
}
.v2026-sysnote-modal__ta {
  flex: 1 1 auto;
  min-height: 160px;
  width: 100%;
  border: 1px solid #EBEDF0;
  border-radius: 12px;
  padding: var(--v2026-sp-3) var(--v2026-sp-4);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  background: #FFFFFF;
  resize: none;
}
.v2026-sysnote-modal__ta:focus {
  outline: none;
  border-color: var(--v2026-color-main);
}

.v2026-sysnote-modal__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-3);
}
.v2026-sysnote-modal__upload {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  padding: 8px 16px;
  border: 1px solid var(--v2026-color-main);
  border-radius: 8px;
  background: rgba(49, 193, 215, 0.08);
  color: var(--v2026-color-main);
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.v2026-sysnote-modal__upload:hover {
  background: rgba(49, 193, 215, 0.18);
}
.v2026-sysnote-modal__upload-ico {
  width: 20px;
  height: 20px;
  background-color: currentColor;
}
.v2026-sysnote-modal__filecount {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--v2026-color-main);
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.v2026-sysnote-modal__admin {
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}
.v2026-sysnote-modal__admin-name {
  font-size: var(--v2026-fs-meta);
  color: var(--v2026-text-secondary);
}

.v2026-sysnote-modal__actions {
  display: flex;
  justify-content: flex-end;
}
.v2026-sysnote-modal__submit {
  min-width: 140px;
  height: 52px;
  padding: 0 28px;
  border: 0;
  border-radius: 6px;
  background: #31C1D7;
  color: #fff;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  cursor: pointer;
}
.v2026-sysnote-modal__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
